import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Mail, Lock, Loader2, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { requestCode, verifyCode } from '../lib/portalAuth';

/**
 * Shared OTP login UI for /broker/login and /owners/login.
 * Step 1 — enter email → Step 2 — enter 6-digit code → redirect to dashboard.
 */
export default function PortalLogin({ portal, title, subtitle, redirectTo }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [busy, setBusy] = useState(false);

  const sendCode = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) { toast.error('Enter your email'); return; }
    setBusy(true);
    try {
      await requestCode(email.trim().toLowerCase(), portal);
      setStep('code');
      toast.success('Code sent — check your email.');
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  const submitCode = async (e) => {
    e?.preventDefault?.();
    if (code.trim().length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setBusy(true);
    try {
      const data = await verifyCode(email.trim().toLowerCase(), portal, code.trim());
      toast.success(`Welcome, ${data.name || 'partner'}`);
      navigate(redirectTo);
    } catch (err) { toast.error(err.message); setCode(''); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col" data-testid={`portal-login-${portal}`}>
      <div className="bg-black text-white">
        <div className="max-w-3xl mx-auto px-5 py-10">
          <Link to="/" className="inline-flex items-center text-xs uppercase tracking-widest text-gray-400 hover:text-white mb-4">
            <ChevronLeft className="h-3 w-3 mr-1" /> Back to ACE
          </Link>
          <p className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold">{portal === 'broker' ? 'ACE · Broker Portal' : 'ACE · Owner Portal'}</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mt-2">{title}</h1>
          <p className="text-sm text-gray-300 mt-2 max-w-lg">{subtitle}</p>
        </div>
      </div>

      <div className="flex-1 -mt-6">
        <div className="max-w-md mx-auto px-5">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
            {step === 'email' && (
              <form onSubmit={sendCode} className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-9 w-9 rounded-lg bg-red-600/10 flex items-center justify-center">
                    <Mail className="h-4 w-4 text-red-600" />
                  </div>
                  <h2 className="font-bold">Sign in with email</h2>
                </div>
                <p className="text-xs text-gray-500">Enter the email associated with your {portal} account. We'll send a 6-digit code.</p>
                <Input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="h-11"
                  autoFocus
                  data-testid={`${portal}-login-email`}
                />
                <Button type="submit" disabled={busy} className="w-full h-11 bg-red-600 hover:bg-red-700 font-bold" data-testid={`${portal}-login-request`}>
                  {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</> : 'Send sign-in code'}
                </Button>
              </form>
            )}
            {step === 'code' && (
              <form onSubmit={submitCode} className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-9 w-9 rounded-lg bg-red-600/10 flex items-center justify-center">
                    <Lock className="h-4 w-4 text-red-600" />
                  </div>
                  <h2 className="font-bold">Enter your code</h2>
                </div>
                <p className="text-xs text-gray-500">A 6-digit code was sent to <span className="font-mono">{email}</span>. The code expires in 10 minutes.</p>
                <Input
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="h-12 text-center text-2xl font-mono tracking-[0.5em]"
                  autoFocus
                  data-testid={`${portal}-login-code`}
                />
                <Button type="submit" disabled={busy} className="w-full h-11 bg-red-600 hover:bg-red-700 font-bold" data-testid={`${portal}-login-verify`}>
                  {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verifying…</> : 'Verify & continue'}
                </Button>
                <button type="button" onClick={() => { setStep('email'); setCode(''); }} className="block w-full text-center text-xs text-gray-500 hover:text-black mt-2">
                  Wrong email? Start over
                </button>
                <button type="button" onClick={sendCode} disabled={busy} className="block w-full text-center text-xs text-blue-600 hover:underline">
                  Resend code
                </button>
              </form>
            )}
          </div>

          <p className="text-[11px] text-gray-500 text-center mt-5 px-3">
            Don't have an account yet?{' '}
            <Link to={portal === 'broker' ? '/intake/broker-submission' : '/intake/owner-onboarding'} className="text-red-600 hover:underline font-bold">
              Apply here
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
